import { DefaultRootState, store } from "@reduxLocal/persistState";
import { View, Text, FlatList, Platform, KeyboardAvoidingView } from "react-native";
import * as Animatable from 'react-native-animatable';
import { FontAwesome5, FontAwesome, AntDesign } from '@expo/vector-icons';
import { connect, useSelector, useDispatch } from 'react-redux'
import { useEffect, useState, useContext, memo } from "react";
import { ActionCreators as UndoActionCreators } from 'redux-undo'
import { HIDE_ALL_MODALS, HIDE_TOOLBAR, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL } from "@reduxLocal/actions/types";
import React from "react";
import { navigationRef } from "@navigation/rootNavigation";
import EditorToolbar from "../../../../components/Global/EditorToolbar/EditorToolbar";
import { ToolbarContext } from "@hooks/toolbarContext";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import { SizableText } from "tamagui";

// Import the new components
import StateHistoryList from "./Components/StateHistoryList";
import HistoryControls from "./Components/HistoryControls";

const mapStateToProps = state => ({
  dataTracker: state.uploadPreview.present,
  dataHistory: state.uploadPreview.past,
  toolbarContext: state.modals.toolbar.toolbarContext,
  itemDragged: state.toolbar.toolbarItemDragged,
  onUndo: () => store.dispatch(UndoActionCreators.undo()),
  onRedo: () => store.dispatch(UndoActionCreators.redo()),
  onReset: () => store.dispatch(UndoActionCreators.clearHistory()),
  currentPage: state.navigation.page.params?.screen,
  currentCompany: state.app.selectedProfile
});

interface ToolbarProps {
  dataHistory?: any,
  toolbarContext?: any,
  onUndo?: () => void,
  onRedo?: () => void,
  onReset?: () => void,
  currentPage?: string,
}

let ToolBar = ({ dataHistory, toolbarContext, onUndo, onRedo, onReset, currentPage }: ToolbarProps) => {
  const [loading, setLoading] = useState(true);
  const { ref, setRef, optionsRef } = useContext(ToolbarContext);
  const theme = themeSelector();
  const dispatch = useDispatch();
  const stateToTrack = toolbarContext?.items?.find(item => item.id === 'history')?.stateName;
  const trackedState = useSelector((state: DefaultRootState) => stateToTrack ? state[stateToTrack]?.past : undefined);

  useEffect(() => {
    if (loading) {
      onReset?.();
      setLoading(false);
    }
    console.log("ToolBar ref type:", typeof ref);
  }, [toolbarContext, ref, optionsRef]);

  // useEffect(() => {
  //   return () => setRef(null);
  // }, [currentPage, ref, optionsRef]);

  const showErrorModal = (message) => {
    dispatch({ type: HIDE_ALL_MODALS });
    dispatch({ type: SHOW_AUTH_MODAL, data: { content: message, type: 'error' } });
  };

  const handleSave = async () => {
    if (!optionsRef?.current) {
      console.error('Options ref not found');
      showErrorModal('Form is not ready. Please try again.');
      return;
    }
    try {
      const validationResult = await optionsRef.current.validateForm?.();

      // Formik's validateForm returns an object with field errors
      // If there are errors, the object has keys. If no errors, it's empty {}
      const hasErrors = validationResult && Object.keys(validationResult).length > 0;

      if (hasErrors) {
        dispatch({ type: HIDE_ALL_MODALS });
        const errorMessages = Object.values(validationResult).join('\n');
        showErrorModal(`Please fix the following errors:\n${errorMessages}`);
      } else {
        // onSubmit handler will show its own loading modal
        await optionsRef.current.handleSubmit?.();
      }
    } catch (e) {
      console.error('Error in handleSave:', e);
      dispatch({ type: HIDE_ALL_MODALS });
      showErrorModal('An error occurred while saving.');
    }
  };

  return (
    <View style={{ backgroundColor: theme ? "white" : "#1a1a1a", paddingBottom: 100, alignSelf: "stretch", height: '100%', paddingTop:20, maxWidth: Platform.OS === 'web' ? 70 : '100%' }} >
      <View style={{ flex: 1, alignSelf: 'stretch' }}>
        <Animatable.View useNativeDriver animation={'fadeIn'} style={{ flex: 5 }}>
          {toolbarContext.historyTracked && (
            <View style={{ padding: 5, alignSelf: 'center', minHeight: 50, minWidth: "100%", backgroundColor: 'grey', shadowRadius: 20, shadowOpacity: .4 }}>
              <FontAwesome5 hitSlop={10} onPress={() => dispatch({ type: HIDE_TOOLBAR })} name="history" style={{ textAlign: 'center', paddingTop: 7 }} size={24} color="white" />
            </View>
          )}

          {toolbarContext.historyTracked ? (
            <StateHistoryList historyData={dataHistory} title="History State" />
          ) : (
            <>
              {toolbarContext.items ? (
                <View style={{ flex: 1, gap: 20, backgroundColor: 'transparent' }}>
                  {toolbarContext.items.find(item => item.id === 'history') && (
                    <StateHistoryList historyData={dataHistory} title="History State" />
                  )}
                  {stateToTrack && (
                    <StateHistoryList historyData={trackedState} title="State" />
                  )}
                  {toolbarContext.items.map((item, index) => {
                    if (item.id === "history") return null;
                    return (
                      <View key={item.toolName + index} style={{ flex: 1 }}>
                        <Animatable.View useNativeDriver animation={'fadeIn'} delay={index * 100} style={{ flex: 1 }}>
                          <View style={{ flexDirection: 'row', left: -3, flex: 1, minWidth: 60, marginVertical: 2, borderRadius: 15, maxHeight: 50, alignContent: 'center' }}>
                            <View style={{ flex: 1, gap: 3 }}>
                              {item.icon === 'slider' ? (
                                <FontAwesome onPress={() => { item.function?.type === 'navigation' ? navigationRef.navigate(item.function.screen) : item.function() }} name="sliders" size={16} color="black" style={{ alignSelf: 'center', overflow: 'visible', padding: 3, top: 3 }} />
                              ) : (
                                <AntDesign onPress={() => { item.function?.type === 'navigation' ? navigationRef.navigate(item.function.screen) : item.function() }} name={item.icon} size={16} color="white" style={{ alignSelf: 'center', overflow: 'visible', padding: 3, top: 3 }} />
                              )}
                              <SizableText numberOfLines={2} selectable={false} flexWrap="wrap" style={{ color: 'white', flexWrap: 'wrap', maxWidth: 50, minHeight: 30, alignSelf: 'center', textAlign: 'center' }}>{item.toolName}</SizableText>
                            </View>
                          </View>
                        </Animatable.View>
                      </View>
                    );
                  })}
                </View>
              ) : (
                ref && (
                  <KeyboardAvoidingView keyboardVerticalOffset={150} style={{  flex: 4, right: 0}}>
                    <EditorToolbar ref={ref} vertical={true} />
                  </KeyboardAvoidingView>
                )
              )}
            </>
          )}
        </Animatable.View>
          {toolbarContext.historyTracked && (
        <View style={{ top: 100, flex: toolbarContext.historyTracked ? 2.5 : 0.0001, zIndex: 1, minHeight: 50 }}>
            <HistoryControls onUndo={onUndo} onRedo={onRedo} onReset={onReset} onSave={handleSave} />
        </View>
          )}
      </View>
    </View>
  );
}

const ConnectedToolBar = connect(mapStateToProps)(ToolBar);
export default memo(ConnectedToolBar);